import { useState, useRef, useEffect } from "react";
import { Check, Camera, ChevronDown, ChevronUp, AlertTriangle, X, Plus, MapPin, Trash2, Home } from "lucide-react";
import { compressPhoto, uploadInspectionPhoto } from "@/api/inspections";

const API = process.env.REACT_APP_BACKEND_URL || "https://propquant-api.onrender.com";

// Shared with handleSubmit and the property-info fetch so all three read the
// same bookingId the same way. Empty string (not "unknown") when the URL
// doesn't have one, matching the property-info effect's existing bail-out.
function getBookingIdFromPath() {
  const pathParts = window.location.pathname.split("/");
  return pathParts[pathParts.indexOf("inspect") + 1] || "";
}

function randomId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ─── BHK Quick Setup Templates ──────────────────────────────────────────────
// When inspector taps "3 BHK", all these locations auto-populate across categories
const BHK_TEMPLATES = {
  "1 BHK": {
    doors: ["Main Entrance", "Bedroom", "Bathroom"],
    windows: ["Living Room", "Bedroom", "Kitchen"],
    flooring: ["Living Room", "Bedroom", "Kitchen", "Bathroom"],
    walls: ["Living Room", "Bedroom", "Kitchen", "Bathroom"],
    dado: ["Bathroom", "Kitchen"],
    plumbing: ["Bathroom", "Kitchen"],
    electrical: ["Living Room", "Bedroom", "Kitchen", "Bathroom"],
    moisture: ["Bathroom", "Kitchen"],
    water_quality: ["Kitchen"],
    measurements: ["Living Room", "Bedroom", "Kitchen", "Bathroom"],
  },
  "2 BHK": {
    doors: ["Main Entrance", "Master Bedroom", "Bedroom 2", "Bathroom 1", "Bathroom 2", "Kitchen"],
    windows: ["Living Room", "Master Bedroom", "Bedroom 2", "Kitchen"],
    flooring: ["Living Room", "Master Bedroom", "Bedroom 2", "Kitchen", "Bathroom 1", "Bathroom 2"],
    walls: ["Living Room", "Master Bedroom", "Bedroom 2", "Kitchen", "Bathroom 1", "Bathroom 2"],
    dado: ["Bathroom 1", "Bathroom 2", "Kitchen"],
    plumbing: ["Bathroom 1", "Bathroom 2", "Kitchen"],
    electrical: ["Living Room", "Master Bedroom", "Bedroom 2", "Kitchen", "Bathroom 1", "Bathroom 2"],
    moisture: ["Bathroom 1", "Bathroom 2", "Kitchen"],
    water_quality: ["Kitchen"],
    measurements: ["Living Room", "Master Bedroom", "Bedroom 2", "Kitchen", "Bathroom 1", "Bathroom 2"],
  },
  "3 BHK": {
    doors: ["Main Entrance", "Master Bedroom", "Bedroom 2", "Bedroom 3", "Bathroom 1", "Bathroom 2", "Kitchen", "Balcony"],
    windows: ["Living Room", "Master Bedroom", "Bedroom 2", "Bedroom 3", "Kitchen", "Balcony"],
    flooring: ["Living Room", "Master Bedroom", "Bedroom 2", "Bedroom 3", "Kitchen", "Bathroom 1", "Bathroom 2", "Passage"],
    walls: ["Living Room", "Master Bedroom", "Bedroom 2", "Bedroom 3", "Kitchen", "Bathroom 1", "Bathroom 2", "Passage"],
    dado: ["Bathroom 1", "Bathroom 2", "Kitchen"],
    plumbing: ["Bathroom 1", "Bathroom 2", "Kitchen"],
    electrical: ["Living Room", "Master Bedroom", "Bedroom 2", "Bedroom 3", "Kitchen", "Bathroom 1", "Bathroom 2"],
    moisture: ["Bathroom 1", "Bathroom 2", "Kitchen", "Master Bedroom"],
    water_quality: ["Kitchen"],
    measurements: ["Living Room", "Master Bedroom", "Bedroom 2", "Bedroom 3", "Kitchen", "Bathroom 1", "Bathroom 2", "Balcony"],
  },
  "4 BHK": {
    doors: ["Main Entrance", "Master Bedroom", "Bedroom 2", "Bedroom 3", "Bedroom 4", "Bathroom 1", "Bathroom 2", "Bathroom 3", "Kitchen", "Balcony 1", "Balcony 2"],
    windows: ["Living Room", "Master Bedroom", "Bedroom 2", "Bedroom 3", "Bedroom 4", "Kitchen", "Balcony 1"],
    flooring: ["Living Room", "Master Bedroom", "Bedroom 2", "Bedroom 3", "Bedroom 4", "Kitchen", "Bathroom 1", "Bathroom 2", "Bathroom 3", "Passage"],
    walls: ["Living Room", "Master Bedroom", "Bedroom 2", "Bedroom 3", "Bedroom 4", "Kitchen", "Bathroom 1", "Bathroom 2", "Bathroom 3", "Passage"],
    dado: ["Bathroom 1", "Bathroom 2", "Bathroom 3", "Kitchen"],
    plumbing: ["Bathroom 1", "Bathroom 2", "Bathroom 3", "Kitchen"],
    electrical: ["Living Room", "Master Bedroom", "Bedroom 2", "Bedroom 3", "Bedroom 4", "Kitchen", "Bathroom 1", "Bathroom 2", "Bathroom 3"],
    moisture: ["Bathroom 1", "Bathroom 2", "Bathroom 3", "Kitchen", "Master Bedroom"],
    water_quality: ["Kitchen"],
    measurements: ["Living Room", "Master Bedroom", "Bedroom 2", "Bedroom 3", "Bedroom 4", "Kitchen", "Bathroom 1", "Bathroom 2", "Bathroom 3", "Balcony 1", "Balcony 2"],
  },
};

// Extra locations an inspector can add manually
const EXTRA_LOCATIONS = {
  doors: ["Utility Room", "Store Room", "Servant Room", "Pooja Room"],
  windows: ["Bathroom 1", "Bathroom 2", "Utility Room", "Servant Room"],
  flooring: ["Balcony", "Utility Room", "Servant Room", "Pooja Room"],
  walls: ["Balcony", "Utility Room", "Servant Room"],
  dado: ["Bathroom 3", "Utility Room"],
  plumbing: ["Utility Room", "Balcony"],
  electrical: ["Balcony", "Utility Room", "Servant Room", "Passage"],
  moisture: ["Living Room", "Bedroom 2", "Bedroom 3", "Balcony"],
  water_quality: ["Bathroom 1", "Bathroom 2"],
  measurements: ["Utility Room", "Servant Room", "Passage", "Pooja Room"],
};

// ─── Full 80-checkpoint checklist ───────────────────────────────────────────
const CHECKLIST = [
  {
    id: "doors", title: "Doors", icon: "🚪", bg: "#EEEDFE", repeatable: true,
    items: [
      { id: "1", label: "Door opens and closes smoothly — no sticking or scraping", how: "Open and close fully — feel for resistance", severity: "high" },
      { id: "2", label: "Door closes on its own when released", how: "Release from 10cm — it should click shut", severity: "high" },
      { id: "3", label: "Door frame has no gaps — sits tight against the wall", how: "Run fingers along all four edges", severity: "high" },
      { id: "4", label: "Door frame is fully finished — no raw wood or rough edges", how: "Check top, bottom, and sides", severity: "high" },
      { id: "5", label: "Door doesn't wobble when pushed sideways", how: "Hold the middle and push left-right", severity: "high" },
      { id: "6", label: "Lock turns smoothly and handle works from both sides", how: "Test from inside and outside", severity: "high" },
      { id: "7", label: "No rust on hinges, screws, or lock parts", how: "Look closely — photograph any rust", severity: "high" },
      { id: "8", label: "Door stopper is present and firmly fixed", how: "Push it — it shouldn't move or wiggle", severity: "moderate" },
      { id: "9", label: "No scratches, dents, or chips on the door surface", how: "Angle your torch across the surface", severity: "moderate" },
      { id: "10", label: "Latch plate sits flat — no gap where door meets frame", how: "Check the door edge where it locks", severity: "moderate" },
    ],
  },
  {
    id: "windows", title: "Windows", icon: "🪟", bg: "#E6F1FB", repeatable: true,
    items: [
      { id: "1", label: "Glass has no cracks, chips, or broken panels", how: "Check every pane including small ventilation panels", severity: "high" },
      { id: "2", label: "Every panel opens, slides, and locks properly", how: "Slide each panel fully open and shut", severity: "high" },
      { id: "3", label: "No grinding or rattling noise when sliding", how: "Slide slowly and listen", severity: "high" },
      { id: "4", label: "Rubber seal between frame and wall is present — no gaps", how: "Run finger around the outer edge on all sides", severity: "high" },
      { id: "5", label: "Window frame is straight — not tilting", how: "Stand 1 meter back and check visually", severity: "high" },
      { id: "6", label: "Rubber strip holding glass is intact — not cracked", how: "Check along all glass edges", severity: "moderate" },
      { id: "7", label: "Mosquito net has no tears or holes", how: "Hold torch behind it and look for gaps", severity: "moderate" },
      { id: "8", label: "No visible scratches on frame or glass", how: "Angle your torch across the surface", severity: "moderate" },
    ],
  },
  {
    id: "flooring", title: "Flooring", icon: "🔲", bg: "#E1F5EE", repeatable: true,
    items: [
      { id: "1", label: "No hollow-sounding tiles when tapped", how: "Knock every tile with your knuckle — hollow ones sound different", severity: "high" },
      { id: "2", label: "No cracked, chipped, or broken tiles", how: "Walk the entire room — photograph damage", severity: "high" },
      { id: "3", label: "Tiles are level — no edges sticking up", how: "Run shoe sole slowly across tile joints", severity: "moderate" },
      { id: "4", label: "Gaps between tiles are evenly filled", how: "Crouch and look across at a low angle", severity: "moderate" },
      { id: "5", label: "No gap between wall base tile and floor", how: "Check all edges of the room", severity: "moderate" },
      { id: "6", label: "Bathroom floor drains properly — no pooling", how: "Pour water — should reach drain within 10 seconds", severity: "high" },
    ],
  },
  {
    id: "walls", title: "Walls & Ceiling", icon: "🧱", bg: "#FAEEDA", repeatable: true,
    items: [
      { id: "1", label: "No cracks — especially near door frames and windows", how: "Check corners and edges — feel for depth", severity: "high" },
      { id: "2", label: "No water stains, damp, or discoloration", how: "Look for yellow-brown marks or peeling paint", severity: "high" },
      { id: "3", label: "No broken or missing plaster", how: "Check behind where the door swings", severity: "high" },
      { id: "4", label: "No construction material left inside frame gaps", how: "Check around all door and window frames", severity: "moderate" },
      { id: "5", label: "Corners and edges intact — no chipping", how: "Check room corners and frame edges", severity: "moderate" },
      { id: "6", label: "No hollow sections behind plaster — wall scanner", how: "Run wall scanner across large wall sections", severity: "high" },
      { id: "7", label: "No wiring in unexpected locations — wall scanner", how: "Scan near planned drilling points like AC areas", severity: "moderate" },
    ],
  },
  {
    id: "painting", title: "Painting", icon: "🎨", bg: "#FBEAF0", repeatable: false,
    items: [
      { id: "1", label: "No fine cracks in the paint near windows", how: "Angle torch flat against wall to reveal cracks", severity: "moderate" },
      { id: "2", label: "Paint is even — no patchy areas or brush marks", how: "Stand 1 meter back and look at an angle", severity: "moderate" },
      { id: "3", label: "No unpainted gaps where walls meet ceiling or frames", how: "Check all joints and frame edges", severity: "moderate" },
      { id: "4", label: "No peeling, bubbling, or flaking paint", how: "Check near bathroom walls and below windows", severity: "high" },
    ],
  },
  {
    id: "dado", title: "Bathroom & Kitchen Wall Tiles", icon: "🔷", bg: "#E1F5EE", repeatable: true,
    items: [
      { id: "1", label: "No hollow-sounding wall tiles when tapped", how: "Knock every tile — hollow ones sound dull", severity: "high" },
      { id: "2", label: "No cracked or chipped wall tiles", how: "Check tile edges and corners first", severity: "moderate" },
      { id: "3", label: "No gap between wall tiles and countertop", how: "Run finger along the joint — should be sealed", severity: "moderate" },
      { id: "4", label: "No gap between wall tiles and tap fittings", how: "Check around every tap and pipe outlet", severity: "moderate" },
      { id: "5", label: "Wall tiles are level — no edges sticking out", how: "Run palm flat across the surface", severity: "high" },
    ],
  },
  {
    id: "woodwork", title: "Woodwork & Cabinets", icon: "🪵", bg: "#FAEEDA", repeatable: false,
    items: [
      { id: "1", label: "All cabinet doors and drawers open smoothly", how: "Open and close every door, drawer, and pull-out", severity: "high" },
      { id: "2", label: "Surface covering is intact — not peeling or bubbling", how: "Check edges and base of all cabinets", severity: "moderate" },
      { id: "3", label: "No cracks in cabinet frames or joints", how: "Check where shelves meet side panels", severity: "moderate" },
    ],
  },
  {
    id: "granite", title: "Granite & Marble Counters", icon: "⬜", bg: "#F1EFE8", repeatable: false,
    items: [
      { id: "1", label: "No cracks in countertops, window sills, or bathroom counters", how: "Run finger along full length — cracks catch your fingernail", severity: "high" },
      { id: "2", label: "Stone is firmly attached — no hollow sound when tapped", how: "Tap along full length and listen", severity: "high" },
      { id: "3", label: "Stone is level with the wall — nothing sticking out", how: "Run palm where stone meets wall", severity: "high" },
    ],
  },
  {
    id: "plumbing", title: "Plumbing", icon: "🔧", bg: "#E6F1FB", repeatable: true,
    items: [
      { id: "1", label: "Flush works fully and refills within 30 seconds", how: "Flush and time refill — check for leaks at base", severity: "high" },
      { id: "2", label: "No leaks at any tap or pipe connection", how: "Turn on each tap — check below for drips", severity: "high" },
      { id: "3", label: "Shower head is firmly fixed — no wobble", how: "Hold and push gently — should be rigid", severity: "high" },
      { id: "4", label: "No rust on toilet bolts, fittings, or pipes", how: "Check base of toilet and under sink", severity: "high" },
      { id: "5", label: "Toilet seat is stable — no sliding or wobble", how: "Check stability carefully", severity: "moderate" },
      { id: "6", label: "No insects or pests near drain or under sink", how: "Open under-sink cabinet and check around pipes", severity: "high" },
      { id: "7", label: "Kitchen tap pressure is good — above 1.5 bar", how: "Use pressure gauge — attach to tap", severity: "high" },
      { id: "8", label: "Bathroom tap pressure is adequate", how: "Use pressure gauge at each tap", severity: "high" },
      { id: "9", label: "Shower pressure is adequate", how: "Use pressure gauge", severity: "moderate" },
      { id: "10", label: "Pressure is consistent across all taps", how: "Compare gauge readings between outlets", severity: "moderate" },
    ],
  },
  {
    id: "electrical", title: "Electrical", icon: "⚡", bg: "#FFF3E0", repeatable: true,
    items: [
      { id: "1", label: "Every socket works and is correctly wired", how: "Plug socket tester into each socket — all three lights should show correct", severity: "high" },
      { id: "2", label: "No reversed wiring at any socket", how: "Socket tester shows if live and neutral are swapped — this is dangerous", severity: "high" },
      { id: "3", label: "Earthing is present and working at every socket", how: "Socket tester shows earthing status — missing = shock risk", severity: "high" },
      { id: "4", label: "Every switch operates its intended light, fan, or point", how: "Flip each switch and verify what it controls", severity: "high" },
      { id: "5", label: "MCB panel is labeled correctly", how: "Open panel — check each breaker label matches actual circuit", severity: "high" },
      { id: "6", label: "MCBs trip when test button is pressed", how: "Press test button on each MCB — should flip off", severity: "high" },
      { id: "7", label: "Safety switch (RCCB/ELCB) trips when tested", how: "Press test button — if it doesn't trip, leakage protection is broken", severity: "high" },
      { id: "8", label: "No exposed wiring or loose connections", how: "Visual check in all rooms — look behind switch plates", severity: "high" },
      { id: "9", label: "Correct wire thickness for geyser, AC, kitchen", how: "Clamp meter or visual — geyser needs 4mm wire, not 2.5mm", severity: "high" },
      { id: "10", label: "Voltage at main board is within safe range (220-240V)", how: "Clamp meter reading at main supply", severity: "moderate" },
    ],
  },
  {
    id: "moisture", title: "Moisture & Leakage", icon: "💧", bg: "#E3F2FD", repeatable: true,
    items: [
      { id: "1", label: "No moisture in wall next to a bathroom", how: "Moisture meter flat on wall — should read below 20%", severity: "high" },
      { id: "2", label: "No moisture below windows", how: "Check wall under each window — rain leaks show here", severity: "high" },
      { id: "3", label: "No moisture in ceiling below upstairs bathroom", how: "If flat above, check ceiling with meter", severity: "high" },
      { id: "4", label: "No visible water stains or damp on walls", how: "Look for yellow-brown marks, bubbling paint, dark patches", severity: "high" },
      { id: "5", label: "No visible water stains or damp on ceiling", how: "Check near bathrooms, kitchen, AC pipe entries", severity: "high" },
      { id: "6", label: "No leakage marks around AC pipe entry", how: "Check wall hole where AC pipes enter — should be sealed", severity: "moderate" },
      { id: "7", label: "No seepage at wall-floor junction in bathrooms", how: "Run moisture meter along base of all bathroom walls", severity: "high" },
      { id: "8", label: "No mold or fungal growth on walls or ceiling", how: "Look in corners, behind doors, near windows", severity: "high" },
    ],
  },
  {
    id: "water_quality", title: "Water Quality", icon: "🚰", bg: "#E0F7FA", repeatable: true,
    items: [
      { id: "1", label: "Kitchen tap TDS is within safe range — below 500 ppm", how: "Fill meter cup from kitchen tap — note reading", severity: "high" },
      { id: "2", label: "Bathroom tap TDS reading", how: "Test and note — compare with kitchen", severity: "moderate" },
      { id: "3", label: "TDS is consistent across all taps", how: "If one reads much higher, possible pipe contamination", severity: "moderate" },
    ],
  },
  {
    id: "measurements", title: "Room Measurements", icon: "📐", bg: "#F3E5F5", repeatable: true,
    items: [
      { id: "1", label: "Room dimensions match seller's claimed area", how: "Laser measure length × width — compare with advertised carpet area", severity: "high" },
      { id: "2", label: "Room measured and documented", how: "Photograph the laser reading in the room", severity: "moderate" },
      { id: "3", label: "Balcony/utility area dimensions verified", how: "These are often exaggerated — measure and record", severity: "moderate" },
    ],
  },
];

const TOTAL_POSSIBLE = CHECKLIST.reduce((s, c) => s + c.items.length, 0);

// Each catalog item already carries a severity ("high"/"moderate") — build a
// {catId: {itemId: severity}} lookup once so submission can stamp it onto
// every checkpoint result without changing the per-item state shape above.
const SEVERITY_BY_ITEM = {};
CHECKLIST.forEach((cat) => {
  SEVERITY_BY_ITEM[cat.id] = {};
  cat.items.forEach((item) => { SEVERITY_BY_ITEM[cat.id][item.id] = item.severity; });
});

// ─── Helper ─────────────────────────────────────────────────────────────────
function blankItems(items) {
  const obj = {};
  items.forEach((item) => { obj[item.id] = { status: null, notes: "", photos: [] }; });
  return obj;
}

// Stamp the catalog severity onto each checkpoint result right before
// submission, so /report reads severity straight off the saved row instead
// of guessing it. Also flattens each item's photos from the in-progress
// {id,status,url,blob} objects the UI tracks down to plain URL strings —
// the only shape that ever leaves the browser. Doesn't touch component
// state — just the outgoing payload.
function withSeverity(instances) {
  const out = {};
  Object.entries(instances).forEach(([catId, insts]) => {
    out[catId] = insts.map((inst) => {
      const items = {};
      Object.entries(inst.items).forEach(([itemId, data]) => {
        items[itemId] = {
          ...data,
          severity: SEVERITY_BY_ITEM[catId]?.[itemId] || data.severity || null,
          photos: (data.photos || []).filter((p) => p.status === "done" && p.url).map((p) => p.url),
        };
      });
      return { ...inst, items };
    });
  });
  return out;
}

// True while any photo anywhere in the form is still uploading or failed —
// used to block submit (with a clear reason) until every photo has either
// succeeded or been removed/retried.
function hasPendingPhotos(instances) {
  for (const insts of Object.values(instances)) {
    for (const inst of insts) {
      for (const d of Object.values(inst.items)) {
        if ((d.photos || []).some((p) => p.status === "uploading" || p.status === "failed")) return true;
      }
    }
  }
  return false;
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function InspectorForm() {
  // ── State ──────────────────────────────────────────────────────
  const [step, setStep] = useState("setup"); // "setup" | "inspect" | "submitted"
  const [bhk, setBhk] = useState(null);
  const [inspectorName, setInspectorName] = useState("");
  const [propertyInfo, setPropertyInfo] = useState({ name: "", address: "", reference: "" });

  // instances: { [catId]: [{ location, items: { [itemId]: {status,notes,photos} } }] }
  const [instances, setInstances] = useState(() => {
    const init = {};
    CHECKLIST.forEach((cat) => {
      init[cat.id] = cat.repeatable ? [] : [{ location: null, items: blankItems(cat.items) }];
    });
    return init;
  });

  const [openCats, setOpenCats] = useState({});
  const [openInstances, setOpenInstances] = useState({});
  const [addingTo, setAddingTo] = useState(null);
  const [customLocation, setCustomLocation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [photoPreview, setPhotoPreview] = useState(null);
  const [activePhotoTarget, setActivePhotoTarget] = useState(null);
  const fileInputRef = useRef(null);

  // ── Fetch property info ────────────────────────────────────────
  useEffect(() => {
    const bookingId = getBookingIdFromPath();
    if (!bookingId || bookingId === "report") return;

    (async () => {
      try {
        const res = await fetch(`${API}/inspection/booking-info/${bookingId}`);
        if (res.ok) {
          const data = await res.json();
          setPropertyInfo(data);
        }
      } catch (e) { /* silent */ }
    })();
  }, []);

  // ── Counts ─────────────────────────────────────────────────────
  const totalChecked = () => {
    let c = 0;
    Object.values(instances).forEach((insts) =>
      insts.forEach((inst) => Object.values(inst.items).forEach((d) => { if (d.status) c++; }))
    );
    return c;
  };

  const totalItems = () => {
    let c = 0;
    CHECKLIST.forEach((cat) => { c += instances[cat.id].length * cat.items.length; });
    return c;
  };

  const totalFailed = () => {
    let c = 0;
    Object.values(instances).forEach((insts) =>
      insts.forEach((inst) => Object.values(inst.items).forEach((d) => { if (d.status === "fail") c++; }))
    );
    return c;
  };

  const catCounts = (catId) => {
    const cat = CHECKLIST.find((c) => c.id === catId);
    const insts = instances[catId];
    let checked = 0, fails = 0, total = insts.length * cat.items.length;
    insts.forEach((inst) => Object.values(inst.items).forEach((d) => {
      if (d.status) checked++;
      if (d.status === "fail") fails++;
    }));
    return { checked, fails, total, count: insts.length };
  };

  // ── BHK Quick Setup ────────────────────────────────────────────
  const applyBhkTemplate = (bhkChoice) => {
    setBhk(bhkChoice);
    const template = BHK_TEMPLATES[bhkChoice];
    if (!template) return;

    const newInstances = {};
    CHECKLIST.forEach((cat) => {
      if (!cat.repeatable) {
        newInstances[cat.id] = [{ location: null, items: blankItems(cat.items) }];
      } else {
        const locations = template[cat.id] || [];
        newInstances[cat.id] = locations.map((loc) => ({ location: loc, items: blankItems(cat.items) }));
      }
    });
    setInstances(newInstances);
    // Auto-open first category
    setOpenCats({ doors: true });
  };

  // ── Instance management ────────────────────────────────────────
  const addInstance = (catId, location) => {
    const cat = CHECKLIST.find((c) => c.id === catId);
    setInstances((prev) => ({
      ...prev,
      [catId]: [...prev[catId], { location, items: blankItems(cat.items) }],
    }));
    setOpenCats((p) => ({ ...p, [catId]: true }));
    const newIdx = instances[catId].length;
    setOpenInstances((p) => ({ ...p, [`${catId}_${newIdx}`]: true }));
    setAddingTo(null);
    setCustomLocation("");
  };

  const removeInstance = (catId, idx) => {
    if (!window.confirm("Remove this location?")) return;
    setInstances((prev) => ({ ...prev, [catId]: prev[catId].filter((_, i) => i !== idx) }));
  };

  // ── Item handlers ──────────────────────────────────────────────
  const setStatus = (catId, instIdx, itemId, status) => {
    setInstances((prev) => {
      const u = { ...prev };
      const inst = { ...u[catId][instIdx] };
      inst.items = { ...inst.items, [itemId]: { ...inst.items[itemId], status: inst.items[itemId].status === status ? null : status } };
      u[catId] = [...u[catId]];
      u[catId][instIdx] = inst;
      return u;
    });
  };

  const setNotes = (catId, instIdx, itemId, notes) => {
    setInstances((prev) => {
      const u = { ...prev };
      const inst = { ...u[catId][instIdx] };
      inst.items = { ...inst.items, [itemId]: { ...inst.items[itemId], notes } };
      u[catId] = [...u[catId]];
      u[catId][instIdx] = inst;
      return u;
    });
  };

  const triggerPhoto = (catId, instIdx, itemId) => {
    setActivePhotoTarget({ catId, instIdx, itemId });
    fileInputRef.current?.click();
  };

  // Applies fn(currentPhotos) -> nextPhotos for one checkpoint item. Every
  // photo state transition (add placeholder, mark done, mark failed, retry,
  // remove) goes through this so they share one update path.
  const updatePhotos = (catId, instIdx, itemId, fn) => {
    setInstances((prev) => {
      const u = { ...prev };
      const inst = { ...u[catId][instIdx] };
      const item = { ...inst.items[itemId] };
      item.photos = fn(item.photos);
      inst.items = { ...inst.items, [itemId]: item };
      u[catId] = [...u[catId]];
      u[catId][instIdx] = inst;
      return u;
    });
  };

  const uploadToCheckpoint = async (catId, instIdx, itemId, photoId, blob) => {
    const bookingId = getBookingIdFromPath() || "unknown";
    const { url, error } = await uploadInspectionPhoto(bookingId, blob);
    if (error || !url) {
      updatePhotos(catId, instIdx, itemId, (photos) =>
        photos.map((p) => (p.id === photoId ? { ...p, status: "failed" } : p))
      );
      return;
    }
    updatePhotos(catId, instIdx, itemId, (photos) =>
      photos.map((p) => (p.id === photoId ? { ...p, status: "done", url, blob: null } : p))
    );
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !activePhotoTarget) return;
    const { catId, instIdx, itemId } = activePhotoTarget;
    const photoId = randomId();

    // Show the upload in flight immediately — don't wait on compression.
    updatePhotos(catId, instIdx, itemId, (photos) => [
      ...photos,
      { id: photoId, status: "uploading", url: null, blob: null },
    ]);

    try {
      const blob = await compressPhoto(file);
      // Stash the compressed blob in state before uploading so a failed
      // upload can be retried without recompressing from the original file.
      updatePhotos(catId, instIdx, itemId, (photos) =>
        photos.map((p) => (p.id === photoId ? { ...p, blob } : p))
      );
      await uploadToCheckpoint(catId, instIdx, itemId, photoId, blob);
    } catch (err) {
      updatePhotos(catId, instIdx, itemId, (photos) =>
        photos.map((p) => (p.id === photoId ? { ...p, status: "failed" } : p))
      );
    }
  };

  const retryPhoto = async (catId, instIdx, itemId, photoId, blob) => {
    if (!blob) return; // nothing to retry against — shouldn't happen, but guard anyway
    updatePhotos(catId, instIdx, itemId, (photos) =>
      photos.map((p) => (p.id === photoId ? { ...p, status: "uploading" } : p))
    );
    await uploadToCheckpoint(catId, instIdx, itemId, photoId, blob);
  };

  const removePhoto = (catId, instIdx, itemId, pi) => {
    updatePhotos(catId, instIdx, itemId, (photos) => photos.filter((_, i) => i !== pi));
  };

  // ── Submit ─────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!inspectorName.trim()) { setError("Enter your name"); return; }
    if (hasPendingPhotos(instances)) {
      setError("Some photos are still uploading or failed to upload. Wait for them to finish, or retry/remove the failed ones, before submitting.");
      return;
    }
    const checked = totalChecked();
    const items = totalItems();
    if (checked < items) {
      if (!window.confirm(`${items - checked} items not checked yet. Submit anyway?`)) return;
    }

    setSubmitting(true);
    setError("");
    const bookingId = getBookingIdFromPath() || "unknown";
    const passed = Object.values(instances).reduce((s, insts) =>
      s + insts.reduce((s2, inst) => s2 + Object.values(inst.items).filter((d) => d.status === "pass").length, 0), 0);
    const failed = totalFailed();

    const payload = {
      booking_id: bookingId,
      inspector_name: inspectorName.trim(),
      checkpoints: withSeverity(instances),
      total_items: items,
      passed, failed,
      skipped: items - checked,
    };
    const body = JSON.stringify(payload);
    // Photos are now URLs (uploaded to Storage already), not base64 — this
    // should read a few KB, not MB. Left in intentionally for now.
    console.log(`[inspection submit] payload size: ${(body.length / 1024).toFixed(1)} KB`);

    try {
      const res = await fetch(`${API}/inspection/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body,
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Failed"); }
      setStep("submitted");
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  const checked = totalChecked();
  const items = totalItems();
  const failed = totalFailed();
  const pct = items > 0 ? Math.round((checked / items) * 100) : 0;
  const pendingPhotos = hasPendingPhotos(instances);

  // ═══════════════════════════════════════════════════════════════
  // SETUP SCREEN
  // ═══════════════════════════════════════════════════════════════
  if (step === "setup") {
    return (
      <div style={S.page}>
        <div style={S.maxW}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "16px 0 24px" }}>
            <img src="/logo.png" alt="" style={{ width: 24, height: 24, borderRadius: 5 }} />
            <span style={{ fontFamily: "'Sora'", fontWeight: 600, fontSize: 14, color: "#1A1917" }}>Propspector</span>
            <span style={{ fontSize: 12, color: "#8A8F90" }}>Inspection</span>
          </div>

          {/* Property info */}
          {propertyInfo.address && (
            <div style={{ ...S.card, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <MapPin size={14} color="#007A8A" />
                <span style={{ fontSize: 13, color: "#007A8A", fontWeight: 600 }}>Property</span>
              </div>
              <p style={{ fontSize: 16, fontWeight: 600, color: "#1A1917", margin: "0 0 2px" }}>{propertyInfo.address}</p>
              {propertyInfo.name && <p style={{ fontSize: 13, color: "#6A6560", margin: 0 }}>Customer: {propertyInfo.name}</p>}
            </div>
          )}

          {/* Inspector name */}
          <div style={{ ...S.card, marginBottom: 16 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: "#1A1917", margin: "0 0 8px" }}>Your name</p>
            <input value={inspectorName} onChange={(e) => setInspectorName(e.target.value)}
              placeholder="Enter your full name" style={S.input} />
          </div>

          {/* BHK Selection */}
          <div style={S.card}>
            <p style={{ fontSize: 16, fontWeight: 700, color: "#1A1917", margin: "0 0 4px", fontFamily: "'Sora'" }}>
              What's the flat configuration?
            </p>
            <p style={{ fontSize: 13, color: "#8A8F90", margin: "0 0 16px" }}>
              This automatically sets up all rooms and checkpoints
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {["1 BHK", "2 BHK", "3 BHK", "4 BHK"].map((opt) => (
                <button key={opt} onClick={() => applyBhkTemplate(opt)} style={{
                  padding: "18px 12px", borderRadius: 12, cursor: "pointer", textAlign: "center",
                  border: bhk === opt ? "2px solid #007A8A" : "2px solid #E7E7E3",
                  background: bhk === opt ? "#EEF9FA" : "#FFFFFF",
                  transition: "all 0.15s",
                }}>
                  <Home size={20} color={bhk === opt ? "#007A8A" : "#8A8F90"} style={{ marginBottom: 6 }} />
                  <div style={{ fontFamily: "'Sora'", fontWeight: 700, fontSize: 16, color: bhk === opt ? "#007A8A" : "#1A1917" }}>{opt}</div>
                </button>
              ))}
            </div>

            {bhk && (
              <div style={{ marginTop: 16 }}>
                <p style={{ fontSize: 12, color: "#8A8F90", margin: "0 0 8px" }}>
                  Auto-added {Object.values(instances).reduce((s, i) => s + i.length, 0)} locations across {CHECKLIST.filter(c => instances[c.id].length > 0).length} categories · {items} checkpoints
                </p>
                <button onClick={() => { if (inspectorName.trim()) setStep("inspect"); else setError("Enter your name first"); }}
                  style={{ ...S.btn, width: "100%", padding: "14px", fontSize: 15 }}>
                  Start Inspection
                </button>
                {error && <p style={{ color: "#B4321F", fontSize: 13, marginTop: 8 }}>{error}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // SUCCESS SCREEN
  // ═══════════════════════════════════════════════════════════════
  if (step === "submitted") {
    return (
      <div style={S.page}>
        <div style={{ ...S.maxW, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "70vh", textAlign: "center" }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#D1FAE5", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
            <Check size={28} color="#065F46" />
          </div>
          <h2 style={{ fontFamily: "'Sora'", fontWeight: 700, fontSize: 20, color: "#1A1917", marginBottom: 6 }}>Report submitted</h2>
          {propertyInfo.address && <p style={{ fontSize: 14, color: "#6A6560", marginBottom: 8 }}>{propertyInfo.address}</p>}
          <p style={{ fontSize: 14, color: "#8A8F90", maxWidth: 320, lineHeight: 1.6 }}>
            {checked} items checked, {failed} issues found. The customer will receive their report once our team verifies it.
          </p>
          <div style={{ display: "flex", gap: 8, marginTop: 24 }}>
            <button onClick={() => window.location.href = "/"} style={S.btn}>Start New Inspection</button>
            <button onClick={() => window.open("https://wa.me/919876543210", "_blank")}
              style={{ ...S.ghost, display: "flex", alignItems: "center", gap: 6 }}>Contact Admin</button>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // INSPECTION FORM
  // ═══════════════════════════════════════════════════════════════
  return (
    <div style={S.page}>
      <div style={S.maxW}>

        <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} style={{ display: "none" }} />

        {photoPreview && (
          <div onClick={() => setPhotoPreview(null)} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.9)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 16 }}>
            <img src={photoPreview} alt="" style={{ maxWidth: "100%", maxHeight: "85vh", borderRadius: 8 }} />
          </div>
        )}

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 0 8px" }}>
          <img src="/logo.png" alt="" style={{ width: 22, height: 22, borderRadius: 5 }} />
          <span style={{ fontFamily: "'Sora'", fontWeight: 600, fontSize: 13, color: "#1A1917" }}>Propspector</span>
        </div>

        {/* Property + progress card */}
        <div style={{ ...S.card, marginBottom: 12 }}>
          {propertyInfo.address && (
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1A1917", margin: "0 0 8px" }}>{propertyInfo.address}</p>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: "#8A8F90" }}>{checked}/{items} checked</span>
            {failed > 0 && <span style={{ fontSize: 12, color: "#B4321F" }}>{failed} issues found</span>}
          </div>
          <div style={{ height: 5, borderRadius: 3, background: "#E7E7E3", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: failed > 0 ? "#A96C11" : "#157F4A", transition: "width 0.3s" }} />
          </div>
        </div>

        {/* Categories */}
        {CHECKLIST.map((cat) => {
          const counts = catCounts(cat.id);
          const isOpen = !!openCats[cat.id];
          const insts = instances[cat.id];

          return (
            <div key={cat.id} style={{ ...S.card, marginBottom: 8, padding: 0 }}>
              {/* Category header */}
              <div onClick={() => setOpenCats((p) => ({ ...p, [cat.id]: !p[cat.id] }))}
                style={{ display: "flex", alignItems: "center", padding: "12px 14px", cursor: "pointer" }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: cat.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, marginRight: 10, flexShrink: 0 }}>
                  {cat.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'Sora'", fontWeight: 600, fontSize: 14, color: "#1A1917" }}>{cat.title}</div>
                  <div style={{ fontSize: 11, color: "#8A8F90" }}>
                    {cat.repeatable
                      ? insts.length === 0 ? "No locations added" : `${insts.length} location${insts.length > 1 ? "s" : ""} · ${counts.checked}/${counts.total}`
                      : `${counts.checked}/${counts.total} checked`
                    }
                    {counts.fails > 0 && <span style={{ color: "#B4321F" }}> · {counts.fails} failed</span>}
                  </div>
                </div>
                {counts.checked === counts.total && counts.total > 0 && (
                  <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600, background: "#D1FAE5", color: "#065F46", marginRight: 6 }}>Done</span>
                )}
                {isOpen ? <ChevronUp size={16} color="#8A8F90" /> : <ChevronDown size={16} color="#8A8F90" />}
              </div>

              {isOpen && (
                <div style={{ borderTop: "1px solid #E7E7E3" }}>
                  {/* Add location button */}
                  {cat.repeatable && (
                    <div style={{ padding: "10px 14px" }}>
                      {addingTo === cat.id ? (
                        <div>
                          <p style={{ fontSize: 12, fontWeight: 600, color: "#1A1917", margin: "0 0 8px" }}>Add location:</p>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {[...(EXTRA_LOCATIONS[cat.id] || [])].filter((loc) => !insts.some((i) => i.location === loc)).map((loc) => (
                              <button key={loc} onClick={() => addInstance(cat.id, loc)} style={S.presetBtn}>{loc}</button>
                            ))}
                          </div>
                          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                            <input value={customLocation} onChange={(e) => setCustomLocation(e.target.value)}
                              placeholder="Or type location name" style={{ ...S.input, flex: 1, fontSize: 12 }} />
                            <button onClick={() => { if (customLocation.trim()) addInstance(cat.id, customLocation.trim()); }}
                              disabled={!customLocation.trim()} style={{ ...S.btn, opacity: customLocation.trim() ? 1 : 0.4, fontSize: 12, padding: "6px 14px" }}>Add</button>
                          </div>
                          <button onClick={() => setAddingTo(null)} style={{ fontSize: 11, color: "#8A8F90", background: "none", border: "none", cursor: "pointer", marginTop: 6 }}>Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => setAddingTo(cat.id)} style={S.addBtn}>
                          <Plus size={14} /> Add location
                        </button>
                      )}
                    </div>
                  )}

                  {/* Instances */}
                  {insts.map((inst, instIdx) => {
                    const instKey = `${cat.id}_${instIdx}`;
                    const instOpen = !!openInstances[instKey];
                    const ic = Object.values(inst.items).filter((d) => d.status).length;
                    const if_ = Object.values(inst.items).filter((d) => d.status === "fail").length;

                    return (
                      <div key={instKey} style={{ borderTop: "1px solid #E7E7E3" }}>
                        {cat.repeatable && (
                          <div onClick={() => setOpenInstances((p) => ({ ...p, [instKey]: !p[instKey] }))}
                            style={{ display: "flex", alignItems: "center", padding: "8px 14px", cursor: "pointer", background: "#FAFAF8" }}>
                            <MapPin size={12} color="#007A8A" style={{ marginRight: 6 }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#1A1917", flex: 1 }}>{inst.location}</span>
                            <span style={{ fontSize: 10, color: "#8A8F90", marginRight: 6 }}>{ic}/{cat.items.length}{if_ > 0 && <span style={{ color: "#B4321F" }}> · {if_}✗</span>}</span>
                            <button onClick={(e) => { e.stopPropagation(); removeInstance(cat.id, instIdx); }}
                              style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: "#C4C0BA" }}>
                              <Trash2 size={12} />
                            </button>
                            {instOpen ? <ChevronUp size={13} color="#8A8F90" /> : <ChevronDown size={13} color="#8A8F90" />}
                          </div>
                        )}

                        {(!cat.repeatable || instOpen) && cat.items.map((item) => {
                          const d = inst.items[item.id];
                          return (
                            <div key={item.id} style={{ padding: "10px 14px", borderBottom: "1px solid #F0EDEA" }}>
                              <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                                <div style={{ width: 6, height: 6, borderRadius: "50%", background: item.severity === "high" ? "#B4321F" : "#A96C11", marginTop: 6, flexShrink: 0 }} />
                                <div style={{ flex: 1 }}>
                                  <p style={{ fontSize: 13, fontWeight: 500, color: "#1A1917", margin: 0, lineHeight: 1.4 }}>{item.label}</p>
                                  <p style={{ fontSize: 11, color: "#8A8F90", marginTop: 2, margin: 0 }}>{item.how}</p>
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: 6, marginLeft: 14 }}>
                                {[["pass", "✓ OK", "22,163,74"], ["fail", "✗ Problem", "180,50,31"], ["na", "Skip", "138,143,144"]].map(([val, label, rgb]) => (
                                  <button key={val} onClick={() => setStatus(cat.id, instIdx, item.id, val)} style={{
                                    padding: "6px 12px", borderRadius: 50, fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
                                    background: d.status === val ? `rgba(${rgb},0.12)` : "#F5F3F0",
                                    color: d.status === val ? `rgb(${rgb})` : "#6A6560",
                                    border: d.status === val ? `1px solid rgba(${rgb},0.25)` : "1px solid transparent",
                                  }}>{label}</button>
                                ))}
                              </div>

                              {(d.status === "fail" || d.notes) && (
                                <textarea value={d.notes} onChange={(e) => setNotes(cat.id, instIdx, item.id, e.target.value)}
                                  placeholder="What did you find?" rows={2}
                                  style={{ ...S.input, marginTop: 6, marginLeft: 14, width: "calc(100% - 14px)", fontSize: 12, resize: "none" }} />
                              )}

                              <div style={{ marginTop: 8, marginLeft: 14 }}>
                                <button onClick={() => triggerPhoto(cat.id, instIdx, item.id)} style={{
                                  display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 50, cursor: "pointer",
                                  fontSize: 12, fontWeight: 600, transition: "all 0.15s",
                                  background: d.status === "fail" && d.photos.length === 0 ? "rgba(180,50,31,0.08)" : "#F5F3F0",
                                  color: d.status === "fail" && d.photos.length === 0 ? "#B4321F" : "#6A6560",
                                  border: d.status === "fail" && d.photos.length === 0 ? "1px solid rgba(180,50,31,0.15)" : "1px solid transparent",
                                }}>
                                  <Camera size={13} />
                                  {d.photos.length > 0 ? `${d.photos.length} photo${d.photos.length > 1 ? "s" : ""} · Add more` : "Add Photo"}
                                </button>
                                {d.status === "fail" && d.photos.length === 0 && (
                                  <p style={{ fontSize: 10, color: "#B4321F", marginTop: 3, marginLeft: 2 }}>Problems should be photographed</p>
                                )}
                              </div>

                              {d.photos.length > 0 && (
                                <div style={{ display: "flex", gap: 6, marginTop: 6, marginLeft: 14, flexWrap: "wrap" }}>
                                  {d.photos.map((photo, pi) => (
                                    <div key={photo.id} style={{ position: "relative", width: 56, height: 56, borderRadius: 8, overflow: "hidden", border: "1px solid #E7E7E3" }}>
                                      {photo.status === "uploading" && (
                                        <div className="ir-form-shimmer" style={{ width: "100%", height: "100%" }} />
                                      )}
                                      {photo.status === "done" && (
                                        <img src={photo.url} alt="" loading="lazy" decoding="async"
                                          onClick={() => setPhotoPreview(photo.url)}
                                          style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }} />
                                      )}
                                      {photo.status === "failed" && (
                                        <button onClick={() => retryPhoto(cat.id, instIdx, item.id, photo.id, photo.blob)}
                                          title="Upload failed — tap to retry"
                                          style={{
                                            width: "100%", height: "100%", background: "rgba(180,50,31,0.08)", border: "none", cursor: "pointer",
                                            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, padding: 0,
                                          }}>
                                          <AlertTriangle size={14} color="#B4321F" />
                                          <span style={{ fontSize: 8, color: "#B4321F", fontWeight: 700 }}>Retry</span>
                                        </button>
                                      )}
                                      {photo.status !== "uploading" && (
                                        <button onClick={() => removePhoto(cat.id, instIdx, item.id, pi)}
                                          style={{ position: "absolute", top: 2, right: 2, width: 18, height: 18, borderRadius: "50%", background: "rgba(0,0,0,0.6)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                          <X size={10} />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {pendingPhotos && !error && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 8, marginTop: 12, background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E", fontSize: 13 }}>
            <AlertTriangle size={14} /> Some photos are still uploading or need a retry — submit unlocks once they're all done.
          </div>
        )}

        {error && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 8, marginTop: 12, background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontSize: 13 }}>
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        <button onClick={handleSubmit} disabled={submitting || pendingPhotos}
          style={{ ...S.btn, marginTop: 14, width: "100%", padding: "14px", fontSize: 15, opacity: submitting || pendingPhotos ? 0.5 : 1, cursor: submitting || pendingPhotos ? "not-allowed" : "pointer" }}>
          {submitting ? "Submitting..." : pendingPhotos ? "Waiting on photos…" : `Submit Report · ${checked}/${items} checked`}
        </button>

        <div style={{ height: 60 }} />
      </div>

      <style>{`
        .ir-form-shimmer {
          background: linear-gradient(90deg, #EDEBE7 25%, #F5F3F0 37%, #EDEBE7 63%);
          background-size: 400% 100%;
          animation: ir-form-shimmer-move 1.4s ease infinite;
        }
        @keyframes ir-form-shimmer-move {
          0% { background-position: 100% 50%; }
          100% { background-position: 0 50%; }
        }
      `}</style>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const S = {
  page: { minHeight: "100vh", background: "#F4F4F1", padding: "0 16px 40px", fontFamily: "'Manrope', sans-serif", color: "#1A1917" },
  maxW: { maxWidth: 480, margin: "0 auto" },
  card: { background: "#FFFFFF", borderRadius: 14, padding: 16, border: "1px solid #E7E7E3" },
  input: {
    width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box",
    border: "1px solid #E7E7E3", background: "#FAFAF8", color: "#1A1917", fontFamily: "'Manrope', sans-serif",
  },
  btn: {
    padding: "10px 20px", borderRadius: 50, border: "none", background: "#007A8A", color: "#FFFFFF",
    fontWeight: 700, fontSize: 14, cursor: "pointer", transition: "all 0.2s", fontFamily: "'Sora', sans-serif",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
  },
  ghost: {
    background: "none", border: "1px solid #E7E7E3", color: "#6A6560",
    fontSize: 13, fontWeight: 600, padding: "8px 16px", borderRadius: 50, cursor: "pointer",
  },
  addBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%",
    padding: "10px", borderRadius: 50, cursor: "pointer", fontSize: 13, fontWeight: 600,
    background: "#FFFFFF", color: "#007A8A", border: "1px dashed #007A8A",
  },
  presetBtn: {
    padding: "6px 12px", borderRadius: 50, border: "1px solid #E7E7E3", fontSize: 12,
    fontWeight: 500, cursor: "pointer", background: "#FFFFFF", color: "#1A1917",
  },
};
